import { ArrowLeft, Flame, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { txt } from "@/lib/format";
import { imgSrc } from "@/lib/media";
import { LangSwitch } from "@/components/LangSwitch";

const iconBtn = "grid h-10 w-10 shrink-0 place-items-center rounded-full transition-colors hover:bg-[color-mix(in_srgb,var(--wt-text)_12%,transparent)]";

const SearchRow = ({ query, setQuery, t }) => (
  <div className="mx-auto max-w-6xl px-4 pb-3 sm:px-6">
    <div className="relative">
      <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--wt-muted)]" />
      <input
        autoFocus
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("pub.searchPlaceholder")}
        aria-label={t("pub.search")}
        data-testid="search-input"
        className="h-11 w-full rounded-full border bg-transparent pe-11 ps-10 text-[15px] outline-none placeholder:text-[var(--wt-muted)] focus:border-[var(--wt-primary)]"
        style={{ borderColor: "color-mix(in srgb, var(--wt-text) 14%, transparent)", background: "var(--wt-surface)" }}
      />
      {query && (
        <button type="button" onClick={() => setQuery("")} aria-label={t("pub.clearSearch")} data-testid="search-clear-btn" className="absolute end-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full hover:bg-white/10">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  </div>
);

export const MenuHeader = ({ s, lang, t, onLang, searchOpen, toggleSearch, query, setQuery, menu, backHref, hideSearch }) => {
  const name = txt(s.brand, "name", lang);
  const tagline = menu ? txt(menu, "name", lang) : txt(s.brand, "tagline", lang);
  const logo = menu?.logo_url || s.brand.logo_url;
  const showSearch = s.menu.search_enabled && !hideSearch;
  return (
    <header
      data-testid="menu-header"
      className="border-b backdrop-blur-xl"
      style={{ background: "color-mix(in srgb, var(--wt-bg) 84%, transparent)", borderColor: "color-mix(in srgb, var(--wt-text) 8%, transparent)", "--ls-active": "var(--wt-primary)" }}
    >
      <div className="mx-auto flex min-h-16 max-w-6xl items-center gap-3 px-4 py-2 sm:px-6">
        {backHref && (
          <Link to={backHref} data-testid="back-to-menus" aria-label={t("pub.allMenus")} title={t("pub.allMenus")} className={`${iconBtn} -ms-2`}>
            <ArrowLeft className="h-5 w-5 rtl:rotate-180" />
          </Link>
        )}
        <a href="#top" className="flex min-w-0 items-center gap-3" data-testid="brand-link">
          {logo ? (
            <img src={imgSrc(logo, "sm")} alt={name} data-testid="brand-logo" style={{ height: s.brand.logo_size, width: "auto", maxWidth: 160 }} className="shrink-0 object-contain" />
          ) : (
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full" style={{ background: "var(--wt-primary)" }}>
              <Flame className="h-5 w-5 text-white" />
            </span>
          )}
          <span className="min-w-0">
            {s.brand.show_name && <span className={`${menu ? "hidden sm:block" : "block"} truncate text-base font-extrabold tracking-tight sm:text-lg`} data-testid="brand-name">{name}</span>}
            {(s.brand.show_tagline || menu) && tagline && <span className={`block truncate ${menu ? "text-sm font-bold sm:text-xs sm:font-normal sm:text-[var(--wt-muted)]" : "text-xs text-[var(--wt-muted)]"}`} data-testid={menu ? "current-menu-name" : "brand-tagline"}>{tagline}</span>}
          </span>
        </a>
        <div className="ms-auto flex items-center gap-1.5 sm:gap-2">
          {showSearch && (
            <button type="button" aria-label={t("pub.search")} aria-expanded={searchOpen} data-testid="search-toggle-btn" onClick={toggleSearch} className={iconBtn}>
              {searchOpen ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
            </button>
          )}
          <LangSwitch lang={lang} onChange={onLang} testid="lang-switch" />
        </div>
      </div>
      {showSearch && searchOpen && <SearchRow query={query} setQuery={setQuery} t={t} />}
    </header>
  );
};
